import React, { useEffect, useState, useCallback } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { toast } from "sonner";
import { LogIn, UserCircle } from "lucide-react";
import { loginWithGoogle } from "../lib/api";
import { writeAuth } from "../lib/auth";

/**
 * Sign-in surface for the site header.
 *
 * Uses `@react-oauth/google`'s `<GoogleLogin>` which renders the official
 * Google Identity Services button and returns an ID-token credential on
 * success. That credential is POSTed to `/api/v1/auth/google`, the server
 * verifies it, and returns our stateless JWT + user object.
 *
 * The preview origin is not yet registered in Google Cloud Console, so
 * GIS will typically fail with "unregistered_origin" or fail silently.
 * We handle both — `<GoogleLogin onError>` and the button falling back
 * to nothing — with a clear toast so the tester never sees a crash.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function SignInMenu() {
  const [expanded, setExpanded] = useState(false);

  const onSuccess = useCallback(async (resp) => {
    try {
      if (!resp?.credential) {
        toast.error("Google didn't return a credential — try again.");
        return;
      }
      const auth = await loginWithGoogle(resp.credential);
      writeAuth({ token: auth.access_token, user: auth.user });
      toast.success(`Signed in as ${auth.user.email}`);
      setExpanded(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Sign-in failed";
      toast.error(`Sign-in failed: ${detail}`);
    }
  }, []);

  const onError = useCallback(() => {
    toast.error(
      "Google sign-in didn't load. If this is the preview URL, its origin " +
      "may not be registered in Google Cloud Console yet.",
    );
  }, []);

  return (
    <div className="relative flex items-center">
      <button
        type="button"
        data-testid="signin-open-btn"
        onClick={() => setExpanded((x) => !x)}
        className="inline-flex h-10 items-center gap-2 rounded-card border border-ink-chip bg-ink-surface px-3 text-body text-ink-text transition-colors duration-cc ease-cc-ease hover:border-ink-muted"
      >
        <LogIn size={16} className="text-ink-muted" />
        Sign in
      </button>
      {expanded && (
        <div
          data-testid="signin-panel"
          className="animate-rise absolute right-0 top-[calc(100%+8px)] z-50 w-[280px] rounded-card border border-ink-chip bg-ink-surface p-4 shadow-[0_16px_36px_-16px_rgba(0,0,0,0.6)]"
        >
          <div className="mb-2 text-eyebrow uppercase text-ink-muted">
            Sign in
          </div>
          <p className="mb-4 text-meta text-ink-muted">
            Sign in with Google to upload showcase entries and vote. Browsing
            is always public.
          </p>
          <div className="flex justify-center" data-testid="google-login-slot">
            <ErrorBoundary onError={onError}>
              <GoogleLogin
                onSuccess={onSuccess}
                onError={onError}
                useOneTap={false}
                theme="filled_black"
                size="large"
                shape="rectangular"
                text="signin_with"
              />
            </ErrorBoundary>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Signed-in user chip: avatar or default icon + display name + menu.
 */
export function UserChip({ user, onSignOut }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="relative flex items-center">
      <button
        type="button"
        data-testid="user-chip-btn"
        onClick={() => setExpanded((x) => !x)}
        className="inline-flex h-10 items-center gap-2 rounded-card border border-ink-chip bg-ink-surface px-3 text-body text-ink-text transition-colors duration-cc ease-cc-ease hover:border-ink-muted"
      >
        {user.avatar_url ? (
          <img
            src={user.avatar_url}
            alt=""
            className="h-6 w-6 rounded-full object-cover"
          />
        ) : (
          <UserCircle size={20} className="text-ink-muted" />
        )}
        <span className="max-w-[140px] truncate">{user.display_name || user.email}</span>
        {user.is_admin && (
          <span className="rounded-chip border border-indigo-chip-border bg-indigo-chip-bg px-2 py-0.5 text-eyebrow uppercase text-indigo-chip-text">
            Admin
          </span>
        )}
      </button>
      {expanded && (
        <div
          data-testid="user-menu"
          className="animate-rise absolute right-0 top-[calc(100%+8px)] z-50 w-[220px] rounded-card border border-ink-chip bg-ink-surface p-2 shadow-[0_16px_36px_-16px_rgba(0,0,0,0.6)]"
        >
          <a
            href={`/users/${user.id}`}
            data-testid="user-menu-profile"
            className="block rounded-chip px-3 py-2 text-body text-ink-text hover:bg-ink-raised"
          >
            Profile
          </a>
          <a
            href="/profile"
            data-testid="user-menu-my-entries"
            className="block rounded-chip px-3 py-2 text-body text-ink-text hover:bg-ink-raised"
          >
            My entries
          </a>
          <button
            data-testid="user-menu-signout"
            className="mt-1 block w-full rounded-chip px-3 py-2 text-left text-body text-ink-muted hover:bg-ink-raised hover:text-ink-text"
            onClick={() => {
              setExpanded(false);
              onSignOut();
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/** Local error boundary so GIS load failure doesn't take down the header. */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(e) {
    if (this.props.onError) this.props.onError(e);
  }
  render() {
    if (this.state.failed) {
      return (
        <div className="text-meta text-ink-muted">
          Google sign-in unavailable.
        </div>
      );
    }
    return this.props.children;
  }
}
